import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { isNil } from 'lodash-es';
import { PreviewPageType } from '@dev/ee-api/ee-api-models';
import { ArrowDropDownIcon, SimplePopover, BasicInput, palette } from '@dev/ee-components';

import { TemplateIncrementAutosaveCounter, TemplateFormPayloadUpdate } from '@/store/paidNewsletterCreator/actions';
import { getLandingPageList, StartLoadingTemplateParams, StartLoadingSubmitLandingPages, StartLoadingAlreadySubscribedLandingPages } from '@/store/landingPageList/actions';
import { debounce } from '@/services/tools/debounce';

import { CustomPopoverBody } from '@/components/FormCreator/steps/Step3EditContent/components/CustomPopoverBody';
import { useLandingPageList } from '@/components/FormCreator/steps/Step3EditContent/hooks/useLandingPageList';
import { useInfinityScrollData } from '@/components/FormCreator/steps/Step3EditContent/hooks/useInfinityScrollData';
import { ArrowDropdownContainer, LandingPageWrapper } from '@/components/FormCreator/steps/Step3EditContent/components/styles';

import { TemplatesList } from '../types';

export const DEFAULT_OPTIONS_COUNT = 10;

interface LandingPageProps {
    pageTemplateType: PreviewPageType;
    idLandingPage: string;
    chosenLandingPage: TemplatesList;
    setChosenLandingPage: (chosenLandingPage: TemplatesList) => void;
    landingPageSearch: string;
    setLandingPageSearch: (landingPageSearch: string) => void;
    landingPagesParams: StartLoadingTemplateParams;
    clientId?: string;
}

export const LandingPageSection = (props: LandingPageProps): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { clientId } = props;
    const { submitLandingPagesList, alreadySubscribedLandingPagesList } = useSelector(getLandingPageList);
    const landingPageList = useLandingPageList(props.pageTemplateType, props.landingPageSearch);
    const { loading, hasMore } = useInfinityScrollData();

    const inputWidth = document.getElementById("landing-dropdown-wrapper")?.offsetWidth || 899;

    const updateTemplates = (search: string, customOffset?: number) => {
        props.landingPagesParams.search = search;
        let offset = 0;

        switch (props.pageTemplateType) {
            case PreviewPageType.AlreadySubscribed:
                offset = customOffset ?? alreadySubscribedLandingPagesList?.length;
                props.landingPagesParams.page = Math.floor(offset/DEFAULT_OPTIONS_COUNT) + 1;
                (hasMore || !offset) && dispatch(StartLoadingAlreadySubscribedLandingPages(props.landingPagesParams));
                break;
            case PreviewPageType.FormSubmit:
                offset = customOffset ?? submitLandingPagesList?.length;
                props.landingPagesParams.page = Math.floor(offset/DEFAULT_OPTIONS_COUNT) + 1;
                (hasMore || !offset) && dispatch(StartLoadingSubmitLandingPages(props.landingPagesParams));
                break;
            default:
                break;
        }
    };

    const onLandingInputChange = (e): void => {
        const value = e?.target?.value || '';
        props.setChosenLandingPage({ label: value, value });
        props.setLandingPageSearch(value);

        debounce((): void => {
            updateTemplates(value, 0);
        });
    };

    const onClickLandingPageDropdown = (e, template): void => {
        if (template.value === "noresults") return;

        props.setChosenLandingPage(template);
        props.setLandingPageSearch(null);
        
        const option = template.value === 'default' ? null : template.label;

        switch (props.pageTemplateType) {
            case PreviewPageType.AlreadySubscribed:
                dispatch(TemplateFormPayloadUpdate(clientId, {FormAlreadySubscribedPage: option}));
                break;
            case PreviewPageType.FormSubmit:
                dispatch(TemplateFormPayloadUpdate(clientId, {FormSubmitPage: option}));
                break;
            default:
                break;
        }

        props.landingPageSearch && updateTemplates(null, 0);
        dispatch(TemplateIncrementAutosaveCounter());
    }

    return (
        <LandingPageWrapper id="landing-dropdown-wrapper">
            <SimplePopover
                id={`formcreator-step3-landing-page-dropdown-${props.idLandingPage}`}
                type="custom"
                disableSearch
                onItemClick={onClickLandingPageDropdown}
                placement="bottom"
                maxHeight={450}
                listItemWidth={inputWidth}
                options={landingPageList as any}
                rowsPerChunk={10}
                cachedListSize={10}
                infiniteScrollProps = {{
                    dataLength: landingPageList.length || DEFAULT_OPTIONS_COUNT,
                    hasMore: hasMore[props.pageTemplateType] ?? true,
                    next: () => updateTemplates(props.landingPageSearch),
                }}
                // eslint-disable-next-line react/no-unstable-nested-components
                customDropdownBody={(element: any): JSX.Element => (
                    <CustomPopoverBody element={element} />
                )}
                isLoadingOptions={loading[props.pageTemplateType]}
            >
                <BasicInput
                    id={`formcreator-step3-landing-page-input-${props.idLandingPage}`}
                    label={t('landingpage')}
                    value={!isNil(props.chosenLandingPage?.label) ? props.chosenLandingPage.label : landingPageList[0].label}
                    onChange={onLandingInputChange}
                    endIcon={
                        <ArrowDropdownContainer>
                            <ArrowDropDownIcon color={palette.indigo[6]} />
                        </ArrowDropdownContainer>
                    }
                />
            </SimplePopover>
        </LandingPageWrapper>
    );
};